import { AppArtBucket, AppArtFile, AppArtFileVariant } from '../types';

/**
 * Os três arquivos de um modelo.
 *
 * O que o cliente vê é a folha; o que a tela precisa para desenhar o mockup ao
 * vivo são os artboards crus. Eles chegam como arquivos irmãos no mesmo bucket,
 * ligados por rodada + `modelKey` — não por ordem nem por nome, que mudam.
 */

const ARTBOARDS = [AppArtFileVariant.welcome, AppArtFileVariant.header];

/** Artboard cru não vira card: ele é insumo do mockup, não um modelo a escolher. */
export const isArtboard = (file: AppArtFile) =>
  !!file.variant && ARTBOARDS.includes(file.variant);

export interface ModelArtboards {
  welcome: AppArtFile | null;
  header: AppArtFile | null;
}

const EMPTY: ModelArtboards = { welcome: null, header: null };

/** Acha os artboards da mesma rodada e do mesmo modelo da folha dada. */
export const artboardsFor = (files: AppArtFile[], model: AppArtFile | null): ModelArtboards => {
  // Sem `modelKey` (upload manual, ou rodada anterior ao campo) não há como
  // saber a qual modelo um artboard pertence — melhor nenhum que o errado.
  if (!model?.modelKey) { return EMPTY; }

  const sibling = (variant: AppArtFileVariant) => files.find(file =>
    file.variant === variant
    && file.modelKey === model.modelKey
    && file.generationRound === model.generationRound) || null;

  return { welcome: sibling(AppArtFileVariant.welcome), header: sibling(AppArtFileVariant.header) };
};

/** As folhas — um card por modelo, sem os artboards crus que alimentam o mockup. */
export const modelSheets = (files: AppArtFile[]) =>
  files.filter(file => file.bucket === AppArtBucket.model && !isArtboard(file));

/**
 * As folhas que o gerador sabe ajustar, da rodada mais nova para a mais velha.
 *
 * Sem `modelKey` não há como dizer QUAL arte refazer — é o caso do upload
 * manual e o das rodadas anteriores ao campo. Some do seletor em vez de virar
 * um comando que o gerador recusa.
 */
export const adjustableModels = (files: AppArtFile[]) =>
  modelSheets(files)
    .filter(file => !!file.modelKey)
    .sort((a, b) => (b.generationRound ?? 0) - (a.generationRound ?? 0));

/**
 * O modelo em foco quando ninguém escolheu um card: o escolhido pelo cliente,
 * senão o mais recente — que é o que a tela já mostra primeiro.
 */
export const focusedModel = (files: AppArtFile[]): AppArtFile | null => {
  const models = modelSheets(files);
  return models.find(file => file.isPrimary) || models[0] || null;
};
